import React, { useCallback, useImperativeHandle, useMemo } from 'react';
import {
  FileUpload,
  type FileUploadSelectEvent,
  type ItemTemplateOptions,
  type FileUploadHeaderTemplateOptions,
  type FileUploadHandlerEvent,
} from 'primereact/fileupload';
import Cancel from '@mui/icons-material/CancelOutlined';
import CheckCircle from '@mui/icons-material/CheckCircleOutline';
import uploadIcon from '../../../../assets/ic_upload_file.png';
import OpenAPI from '../../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import { type components } from '../../../../endpoint/Schema';
import { Icons } from '../../../../assets/icons';

interface IFile {
  name: string;
  objectURL: string;
  size: number;
}

interface ResponseInterface {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  error: unknown;
  response: Response;
}

export interface IImageUploadInstance {
  isFileSelected: () => boolean;
  save: () => Promise<ResponseInterface>;
}

export interface IImageUploadProps {
  appAssetsType: components['schemas']['AppAssetsType'];
  disabled?: boolean;
}

const ImageUpload = React.forwardRef<IImageUploadInstance, IImageUploadProps>(
  ({ appAssetsType, disabled }, ref) => {
    const appAssets = useTenantStore((state) => state.appAssets);
    const tenantId = useTenantStore(selectTenantId);
    const [completed, setCompleted] = React.useState(false);
    const [totalSize, setTotalSize] = React.useState(0);
    const [selectedFile, setSelectedFile] = React.useState<File>();
    const [pending, setPending] = React.useState(false);
    const fileUploadRef = React.useRef<FileUpload>(null);
    const currentImage = useMemo(
      () => appAssets?.find((a) => a.name === appAssetsType)?.url,
      [appAssets, appAssetsType],
    );

    const handleSave = useCallback(async () => {
      if (!selectedFile) return;
      setPending(true);

      try {
        const res = await OpenAPI.POST('/api/v2/admin/tenant/asset', {
          params: {
            query: {
              tenantId,
            },
          },
          body: {
            // @ts-expect-error
            File: selectedFile,
            AppAssetsType: appAssetsType,
          },
          bodySerializer: (body) => {
            if (!body) return body;

            const formData = new FormData();
            if (body.File) formData.set('file', body.File);
            if (body.AppAssetsType)
              formData.set('appAssetsType', body.AppAssetsType);
            return formData;
          },
        });

        setPending(false);
        setCompleted(true);
        return res;
      } catch (err) {
        setPending(false);
        throw err;
      }
    }, [appAssetsType, selectedFile, tenantId]);

    useImperativeHandle(
      ref,
      () => ({
        isFileSelected: () => !!selectedFile && !completed,
        save: handleSave as () => Promise<ResponseInterface>,
      }),
      [completed, handleSave, selectedFile],
    );

    const myUploader = (event: FileUploadHandlerEvent) => {
      setSelectedFile(event.files[0]);
      event.options.clear();
    };

    const onTemplateSelect = (e: FileUploadSelectEvent) => {
      setCompleted(false);
      setSelectedFile(e.files[0]);
      let _totalSize = totalSize;
      const files = e.files;

      files.forEach((file) => {
        _totalSize += file.size || 0;
      });

      setTotalSize(_totalSize);
    };

    const onTemplateRemove = (file: IFile, callback: () => void) => {
      setTotalSize(totalSize - file.size);
      callback();
      setSelectedFile(undefined);
      setCompleted(false);
    };

    const onTemplateClear = () => {
      setTotalSize(0);
    };

    const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
      return (
        <div className="flex flex-row mb-4 align-items-center">
          {options.chooseButton}
        </div>
      );
    };

    const itemTemplate = (file: object, props: ItemTemplateOptions) => {
      const fileName = (file as IFile).name;

      return (
        <div className="flex flex-col border rounded-2xl bg-whiteSmoke px-m16 py-m20">
          <div className="flex justify-between items-center">
            <div className="flex flex-1 items-center text-stormGray">
              <img
                loading="lazy"
                src={Icons.ic_png_file}
                className="aspect-1 w-[3rem] mr-1"
              />
              {fileName.length > 20
                ? fileName.substring(0, 18) + '...'
                : fileName}
            </div>

            <button
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                onTemplateRemove(file as IFile, props.onRemove);
              }}
              // className="flex justify-center items-center border border-stormGray p-0.5 rounded-[2rem]"
            >
              {completed ? (
                <CheckCircle
                  className="text-success"
                  sx={{ fontSize: '2rem' }}
                />
              ) : (
                <Cancel className="text-stormGray" sx={{ fontSize: '2rem' }} />
              )}
            </button>
          </div>
        </div>
      );
    };

    const emptyTemplate = () => {
      return (
        <div className="flex flex-col items-center px-5 py-3.5 mt-5 text-sm leading-5 text-zinc-500">
          <img loading="lazy" src={uploadIcon} className="aspect-1 w-[60px]" />
          <div className="mt-11 text-base font-bold leading-6">
            drag and drop.
          </div>
          <div className="mt-2">Maximum file size is 100MB.</div>
          {appAssetsType === 'SplashScreenXxxHdpi' ? (
            <div className="mt-4">The best size is 1280*1920.</div>
          ) : null}

          <div className="mt-12">File supported: PNG</div>
        </div>
      );
    };

    return (
      <div className="flex flex-col w-full text-base leading-6 text-black rounded-3xl">
        <FileUpload
          ref={fileUploadRef}
          name="icon[]"
          url="/api/upload"
          accept="image/png"
          maxFileSize={100000000}
          onSelect={onTemplateSelect}
          onError={onTemplateClear}
          onClear={onTemplateClear}
          headerTemplate={headerTemplate}
          itemTemplate={itemTemplate}
          emptyTemplate={emptyTemplate}
          chooseOptions={{
            label: 'Click to upload',
            icon: ' ',
            className: '',
          }}
          customUpload={true}
          uploadHandler={myUploader}
          disabled={pending || disabled}
          contentClassName=" rounded-2xl border border-dashed border-neutral-400 px-0 py-0"
        />

        {currentImage && (
          <div className="flex bg-gray border rounded-2xl border-neutral-300 mt-4 p-12 justify-center items-center">
            <img src={currentImage} className="object-contain" />
          </div>
        )}
      </div>
    );
  },
);

ImageUpload.displayName = 'ImageUpload';

export default ImageUpload;
